import React, { useEffect, useState } from "react";
import { MdWallpaper } from "react-icons/md";
import { getWallpaperStatus, updateWallpaper, getWallpaperImageUrl } from "../../../services/wallpaperApi";

export default function WallpaperPage() {
  const [enabled, setEnabled] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ t: "ok" | "err"; m: string } | null>(null);

  const flash = (t: "ok" | "err", m: string) => {
    setMsg({ t, m });
    setTimeout(() => setMsg(null), 3500);
  };

  useEffect(() => {
    (async () => {
      try {
        const s = await getWallpaperStatus();
        setEnabled(s.enabled);
        setUpdatedAt(s.updatedAt);
        if (s.hasImage) setPreview(await getWallpaperImageUrl());
      } catch {
        flash("err", "Não foi possível carregar a configuração.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      flash("err", "Imagem muito grande (máx. 8 MB).");
      return;
    }
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = r.result as string;
      setImageBase64(dataUrl);
      setPreview(dataUrl);
    };
    r.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try {
      const s = await updateWallpaper({ enabled, imageBase64: imageBase64 });
      setUpdatedAt(s.updatedAt);
      setImageBase64(null);
      flash("ok", "Salvo. Os notebooks online reaplicam em até ~10 min (ou no próximo login).");
    } catch {
      flash("err", "Falha ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  // Re-empurra a config atual (sem trocar imagem) → força os notebooks a reaplicar no próximo polling.
  const forceSync = async () => {
    setSaving(true);
    try {
      const s = await updateWallpaper({ enabled });
      setUpdatedAt(s.updatedAt);
      flash("ok", "Sincronização disparada — os notebooks online reaplicam em até ~10 min.");
    } catch {
      flash("err", "Falha ao sincronizar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-5 flex flex-col gap-5 animate-fade-up">
      <div className="flex items-center gap-4">
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-dv-gradient text-white shadow-dv-glow">
          <MdWallpaper className="h-6 w-6" />
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-500">Dispositivos</p>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">Papel de parede corporativo</h1>
        </div>
      </div>

      <p className="max-w-2xl text-sm text-gray-600 dark:text-gray-300">
        Defina a imagem e ative — os widgets instalados nos notebooks aplicam o papel de parede
        automaticamente <b>uma vez</b>, no próximo login (o colaborador ainda pode trocar depois).
      </p>

      {msg && (
        <div className={`rounded-lg px-4 py-2 text-sm ${msg.t === "ok" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
          {msg.m}
        </div>
      )}

      {loading ? (
        <div className="animate-pulse rounded-2xl border border-gray-200 p-10 text-center text-gray-400 dark:border-navy-700">
          Carregando…
        </div>
      ) : (
        <div className="flex max-w-2xl flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 dark:border-navy-700 dark:bg-navy-800">
          <label className="flex items-center gap-3">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-5 w-5 accent-orange-500" />
            <span className="text-sm font-semibold text-navy-700 dark:text-white">Aplicar papel de parede nos notebooks</span>
          </label>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Imagem</label>
            <input type="file" accept="image/png,image/jpeg" onChange={onFile} className="text-sm text-gray-600 dark:text-gray-300" />
          </div>

          {preview ? (
            <img src={preview} alt="preview" className="max-h-56 w-full rounded-xl border border-gray-200 object-cover dark:border-navy-700" />
          ) : (
            <div className="grid h-40 place-items-center rounded-xl border border-dashed border-gray-300 text-sm text-gray-400 dark:border-navy-700">
              Nenhuma imagem definida
            </div>
          )}

          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-400">
              {updatedAt ? `Última alteração: ${new Date(updatedAt).toLocaleString("pt-BR")}` : "Ainda não configurado"}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={forceSync}
                disabled={saving}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-navy-700 disabled:opacity-60 dark:border-navy-600 dark:text-white"
                title="Re-empurra o wallpaper atual para os notebooks (reaplicam no próximo polling, ~10 min)"
              >
                Forçar sincronização
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="rounded-lg bg-dv-gradient px-5 py-2 text-sm font-semibold text-white shadow-dv-glow disabled:opacity-60"
              >
                {saving ? "Salvando…" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
